import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

interface VirtualizedTableComponentProps {
	node?: any;
	children?: React.ReactNode;
	columnWidths?: Array<number>;
	layout?: string;
	renderWidth?: number;
	innerRef?: React.RefObject<HTMLTableElement>;
}

export const VirtualizedTableComponent: React.FC<VirtualizedTableComponentProps> = ({
	node,
	children,
	columnWidths,
	layout,
	renderWidth,
	innerRef,
}) => {
	const parentRef = useRef<HTMLTableElement>(null);
	const tableLayout = node?.attrs?.layout || layout;
	const tableDisplayMode = node?.attrs?.displayMode;
	const tableWidth = node?.attrs?.width || renderWidth;

	const childrenArray = React.Children.toArray(children);
	const rowCount = childrenArray.length;

	const virtualizer = useVirtualizer({
		count: rowCount,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 20, // Estimated row height
		overscan: 5,
	});

	const virtualItems = virtualizer.getVirtualItems();

	return (
		<div
			ref={parentRef}
			data-testid="virtualized-table-container"
			style={{
				height: '300px',
				overflow: 'auto',
			}}
		>
			<div
				style={{
					height: `${virtualizer.getTotalSize()}px`,
					position: 'relative',
				}}
			>
				<table
					data-testid="virtualized-renderer-table"
					data-table-width={tableWidth}
					data-layout={tableLayout}
					data-table-display-mode={tableDisplayMode}
					ref={innerRef}
				>
					{columnWidths && (
						<colgroup>
							{columnWidths.map((width, index) => (
								<col key={index} style={{ width: `${width}px` }} />
							))}
						</colgroup>
					)}
					<tbody>
						{virtualItems.map((virtualRow) => {
							return childrenArray[virtualRow.index];
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};
